<template>
  <div>
    <StepHeader
      :title="title"
      :theme="theme"
    />

    <StepContent
      width="sm"
      :theme="theme"
    >
      <div
        v-if="!invalidResponse"
        class="contact-code__step-content"
      >
        <p class="contact-code__step-content-title">
          Please enter the four digit code we sent you at
        </p>
        <div class="contact-code__step-content-text">
          <span class="mr-2">{{ customer.home_phone }}</span>
          <span
            class="contact-code__step-content-number"
            :class="textColorClass()"
            @click="$router.go(-1)"
          >Change Number</span>
        </div>

        <AppInputWithValidation
          v-for="(field, index) in fields"
          :id="field.name"
          :key="index"
          :label="field.label"
          :name="field.name"
          :type="field.type"
          :mask="field.mask"
          :value="field.value"
          :error="phoneCodeError"
          :max-length="field.maxLength"
          max-width="max-w-170"
          :rules="field.rules"
          :placeholder-char="field.placeholderChar"
          :placeholder="field.placeholder"
          :hide-error="!submitted"
          verification-code
          :theme="theme"
          @input="handleInput($event, field.vuexAction)"
        />

        <div
          class="contact-code__resend-code"
        >
          <AppSpinner
            v-if="resendingCode"
            color="#00BED9"
          />
          <p
            v-else
            class="contact-code__text"
            :class="[resendTextStyles, computedTextStyles]"
            @click="handleResendClick"
          >
            {{ resendText }}
          </p>
        </div>

        <div
          v-if="maxResend"
          class="contact-code__max-resend"
        >
          <FontAwesomeIcon
            class="mr-5"
            icon="circle-exclamation"
          />
          <small>You have exceeded the maximum number of resend attempts. Please try again in 10 minutes.</small>
        </div>
      </div>

      <ContactFailure v-else-if="invalidResponse" />
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="verifyingCode"
        @click="handleContinue"
      >
        Continue
      </AppButton>
    </StepFooter>
  </div>
</template>

<script>
import AppButton from '@/components/common/buttons/AppButton'
import AppInputWithValidation from '@/components/common/inputs/AppInputWithValidation'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepHeader from '@/components/common/step-layout/StepHeader'
import ContactFailure from '@/components/common/notifications/ContactFailure'
import AppSpinner from '@/components/common/spinners/AppSpinner'

import input from '@/mixins/input'
import navigation from '@/mixins/navigation'
import buttonList from '@/mixins/button-list'
import submitLoans from '@/mixins/submit/submit-loans'
import DataLayer from '@/utilities/data-layer'
import theme from '@/mixins/theme'
import step from '@/mixins/step'
import submitMortgage from '@/mixins/submit/submit-mortgage'
import CryptoJS from 'crypto-js'

export default {
  name: 'ContactCodeStandard',

  // Components
  components: {
    AppButton,
    AppInputWithValidation,
    StepContent,
    StepFooter,
    StepHeader,
    ContactFailure,
    AppSpinner
  },

  // Mixins
  mixins: [
    input,
    buttonList,
    navigation,
    submitLoans,
    theme,
    step,
    submitMortgage
  ],

  // Props
  props: {
    title: {
      type: String,
      default: 'How Can We Reach You?'
    }
  },

  // Data
  data () {
    return {
      verifyingCode: false,
      resendingCode: false,
      showSent: false,
      submitted: false,
      verificationStatus: '',
      invalidResponse: false,
      verificationExpired: false,
      maxResend: false,
      countdown: 30
    }
  },

  // Computed
  computed: {

    phoneCodeError () {
      return this.$store.state.validations.phoneCodeError
    },

    resendText () {
      return this.showSent ? '00:' + ('0' + this.countdown).slice(-2) : 'Resend'
    },

    resendTextStyles () {
      return {
        'underline cursor-pointer': !this.showSent
      }
    },

    computedTextStyles () {
      return this.showSent ? '' : this.textColorClass()
    },

    customer () {
      return this.$store.state.customer
    },

    employment () {
      return this.$store.state.employment
    },

    cduid () {
      return this.$store.state.customer.cduid
    },

    fields () {
      return [{
        name: 'code',
        type: 'tel',
        value: this.customer.phoneVerificationCode,
        maxLength: 4,
        rules: this.phoneVerificationCodeRules,
        vuexAction: 'setVerificationCode',
        placeholder: '____'
      }]
    },

    isVerificationStatusApproved () {
      return this.verificationStatus === 'approved'
    },

    isReturningCustomerInProgress () {
      return this.$store.state.returningCustomer.is_in_progress
    },

    phoneVerificationCodeRules () {
      return `required|digits:4|isVerified:${!this.failedToVerify}|verificationExpired:${!this.verificationExpired}`
    },

    failedToVerify () {
      return this.verificationStatus === 'pending' || this.verificationStatus === 'denied'
    },

    updateLeadPayload () {
      return {
        Twilio_Phone_Verified__c: true
      }
    },
    isFoundation () {
      return this.$store.getters['loans/isFoundation']
    }
  },

  // Watch
  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleContinue()
      this.$store.dispatch('setTriggerContinue', false)
    }
  },

  created () {
    // Preventing cx from resending verfication code for 30 sec
    this.showSent = true
    this.resendCountDownTimer()
  },

  // Methods
  methods: {
    handleInput ($event, vuexAction) {
      this.verificationStatus = ''
      this.$_input_updateState(vuexAction, $event)
    },
    handleResendClick () {
      if (!this.showSent) {
        this.resendCode()
      }
    },
    async resendCode () {
      // prevent spamming requests
      if (this.resendingCode) return false
      this.maxResend = false
      this.resendingCode = true
      this.verificationExpired = false
      try {
        await this.$store.dispatch('requestVerification', {
          phone: this.customer.home_phone.replace(/[^0-9]/g, ''),
          channel: this.customer.phoneVerificationMethod
        })
        this.$store.dispatch('incrementVerificationAttempts')
      } catch (error) {
        // Server will return 429 for 10 minutes if code has been sent more than 5 times
        if (error.status && (error.status === 429 || error.status === '429')) {
          this.maxResend = true
          this.resendingCode = false
        }
      } finally {
        if (this.resendingCode) {
          this.resendingCode = false
          this.countdown = 30
          this.showSent = true
          this.resendCountDownTimer()
        }
      }
    },

    // Prevent Cx from sending request to Twilio and show them time they can resend it in
    resendCountDownTimer () {
      setTimeout(() => {
        if (this.countdown > 0) {
          this.countdown--
          this.resendCountDownTimer()
        } else {
          this.showSent = false
        }
      }, 1000)
    },

    async handleContinue () {
      // user clicks continue to submit verification code
      this.submitted = true
      if (this.verifyingCode) return false // prevent spamming requests
      if (this.invalidResponse) {
        if (this.$_navigation_isMortgage) await this.$_submit_mortgageLead()
        this.$_navigation_handleNext()
      } else {
        const isValid = await this.$_navigation_validateStep()
        if (!isValid) return
        await this.sendCodeVerificationRequest()
        if (!this.isVerificationStatusApproved) return

        if (this.$_navigation_isMortgage) await this.$_submit_mortgageLead()
        await this.$_step_handleContinue(this.updateLeadPayload)
        this.verifyingCode = false
      }
    },

    async sendCodeVerificationRequest () {
      const dataLayerObject = {
        event: `spring_${this.eventPrefix}_verification_code_inserted_continue`,
        sha256_phone_number: CryptoJS.SHA256(this.customer.home_phone).toString()
      }

      if (this.eventPrefix === 'ploan') {
        window.rudderanalytics.identify(this.customer.email, {
          phone_number: this.customer.home_phone,
          email: this.customer.email
        })
        window.rudderanalytics.track(`spring_${this.isFoundation ? 'foundation' : 'ploan'}_verification_code_inserted_continue`, {
          employment_status: this.employment.source_of_income || null,
          monthly_income: this.employment.monthly_income || null,
          credit_rating: this.customer.credit_rating || null,
          lead_source: this.leadSource,
          device: this.deviceType,
          returning_customer: this.returningCustomer
        })
      }

      DataLayer.pushObject(dataLayerObject)
      try {
        this.verifyingCode = true
        // call lambda middleware to check the verificaiton code with Twilio
        const verificationStatus = await this.$store.dispatch('checkVerification', {
          verificationSid: this.customer.phoneVerificationSid,
          code: this.customer.phoneVerificationCode
        })
        // Twilio response of approved|pending|denied
        this.verificationStatus = verificationStatus
        // submit lead if the code was approved
        if (this.verificationStatus === 'approved') {
          DataLayer.pushObject({ 'verification_successful': 1 })
          return
        }
        this.verifyingCode = false
        if (this.failedToVerify) {
          this.$store.dispatch('setPhoneCodeError', 'The code you entered is invalid. Please try again.')
        }

        // set an error message if the code is pending/denied
        // increment the failed attempts count and push to the datalayer
        this.$store.dispatch('incrementFailedVerifyAttempts')
        DataLayer.pushEvent('event', `spring_${this.eventPrefix}_verification_code_invalid`)
        DataLayer.pushObject({ verification_unsuccessful_continue: this.customer.failedVerificationAttempts })
      } catch (error) {
        if (error.status && (error.status === 503 || error.status === '503')) {
          // render ContactFailure component if Twilio/our service
          // is down to avoid losing a potential valid lead
          this.invalidResponse = true
        } else if (error.status && (error.status === 404 || error.status === '404')) {
          this.verificationExpired = true
          this.$store.dispatch('setPhoneCodeError', 'Your verification code has expired. Please click Resend to receive a new one.')
        }
        this.verifyingCode = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
.contact-code {
  &__step-content {
    @apply text-center w-full max-w-500 m-auto;
  }

  &__step-content-title {
    @apply text-center inline-block;
  }

  &__step-content-text {
    @apply text-xs font-semibold mt-20 mb-30;

    @screen sm {
      @apply text-base mt-0 mb-40;
    }
  }

  &__step-content-number {
    @apply ml-2 underline cursor-pointer;
  }

  &__resend-code {
    @apply flex justify-center h-40;
  }

  &__max-resend {
    @apply text-red w-full;

    @screen md {
      @apply px-25;
    }
  }
}
</style>
